// Reads from the Google Calendar API with a short-lived access token; Cuplzap
// never writes to Google. On a 401 the caller's token getter is asked for a
// fresh one once and the request retried once. Under the emulators the base is
// the mock API.

import type { RawCalendar, RawEvent } from '@/logic/events'

export const CALENDAR_API_BASE = import.meta.env.VITE_CALENDAR_API_BASE || 'https://www.googleapis.com/calendar/v3'

export type TokenGetter = (force?: boolean) => Promise<string>

export class CalendarApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function authed(getToken: TokenGetter, path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const token = await getToken()
  const res = await fetch(`${CALENDAR_API_BASE}${path}`, {
    ...init,
    headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
  })
  if (res.status === 401 && retry) {
    await getToken(true)
    return authed(getToken, path, init, false)
  }
  if (!res.ok) {
    let message = `${res.status}`
    try {
      const body = (await res.json()) as { error?: { message?: string } }
      message = body.error?.message ?? message
    } catch {
      // no body
    }
    throw new CalendarApiError(res.status, message)
  }
  return res
}

export async function listCalendars(getToken: TokenGetter): Promise<RawCalendar[]> {
  const res = await authed(getToken, '/users/me/calendarList?minAccessRole=reader')
  const body = (await res.json()) as { items?: RawCalendar[] }
  return body.items ?? []
}

/** events.list with singleEvents, within a window, following nextPageToken. */
export async function listEvents(getToken: TokenGetter, calendarId: string, timeMin: Date, timeMax: Date): Promise<RawEvent[]> {
  const out: RawEvent[] = []
  let pageToken: string | undefined
  do {
    const q = new URLSearchParams({
      singleEvents: 'true',
      orderBy: 'startTime',
      timeMin: timeMin.toISOString(),
      timeMax: timeMax.toISOString(),
      maxResults: '250',
    })
    if (pageToken) q.set('pageToken', pageToken)
    const res = await authed(getToken, `/calendars/${encodeURIComponent(calendarId)}/events?${q}`)
    const body = (await res.json()) as { items?: RawEvent[]; nextPageToken?: string }
    out.push(...(body.items ?? []))
    pageToken = body.nextPageToken
  } while (pageToken)
  return out
}

/**
 * events.get: one event, here a series' own event, for its repeat rule. A button waits on it, so it gives
 * up after a few seconds rather than leave the button waiting on a stalled connection.
 */
export async function getEvent(getToken: TokenGetter, calendarId: string, eventId: string): Promise<RawEvent> {
  const res = await authed(getToken, `/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`, { signal: AbortSignal.timeout(6000) })
  return (await res.json()) as RawEvent
}
